import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiClient } from './client';
import { IS_DEMO_MODE } from './demoMode';
import type {
  Alert,
  AlertPolicy,
  AlertQueryParams,
  Aoi,
  ApiResponse,
  Asset,
  ConditionResponse,
  CropCapability,
  CropCapabilityParams,
  CountyGeometryProperties,
  CreateAlertPolicyRequest,
  CreateAoiRequest,
  CreateAssetRequest,
  EventFilterParams,
  EventsResponse,
  FilterParams,
  GenerateReportRequest,
  GeoJsonFeatureCollection,
  GeoMultiPolygon,
  GeoPolygon,
  LoginRequest,
  ModelVersion,
  ModelVersionParams,
  Observation,
  ObservationQueryParams,
  ObservationSummary,
  PaginationParams,
  PredictionParams,
  PredictionValidationResponse,
  PredictionsResponse,
  ProgressResponse,
  CountyDistributionParams,
  CountyDistributionResponse,
  DemoBriefing,
  DemoManifest,
  ReconciliationParams,
  ReconciliationResponse,
  Report,
  Session,
  SignupRequest,
  SoilMoistureResponse,
  Tenant,
  TenantCapabilities,
  UpdateAssetRequest,
} from './types';

export const qk = {
  demoManifest: ['demo', 'manifest'] as const,
  demoBriefing: ['demo', 'briefing'] as const,
  session: ['session'] as const,
  assets: (params?: PaginationParams) => ['assets', params] as const,
  asset: (id: string) => ['assets', id] as const,
  aois: (assetId: string) => ['aois', assetId] as const,
  aoi: (id: string) => ['aois', 'detail', id] as const,
  observations: (params: ObservationQueryParams) => ['observations', params] as const,
  observationsSummary: (assetId: string) => ['observations', 'summary', assetId] as const,
  alerts: (params?: AlertQueryParams) => ['alerts', params] as const,
  alertPolicies: () => ['alert-policies'] as const,
  alertPolicy: (id: string) => ['alert-policies', id] as const,
  reports: () => ['reports'] as const,
  report: (id: string) => ['reports', id] as const,
  tenant: () => ['tenant'] as const,
  tenantCapabilities: () => ['tenant', 'capabilities'] as const,
  cropCapabilities: (params?: CropCapabilityParams) => ['crop-capabilities', params] as const,
  predictions: (params?: PredictionParams) => ['predictions', params] as const,
  predictionValidation: (params?: PredictionParams) => ['predictions', 'validation', params] as const,
  meridianCondition: (params: FilterParams) => ['meridian', 'condition', params] as const,
  meridianProgress: (params: FilterParams) => ['meridian', 'progress', params] as const,
  meridianEvents: (params: EventFilterParams) => ['meridian', 'events', params] as const,
  meridianSoilMoisture: (params: Record<string, unknown>) => ['meridian', 'soil-moisture', params] as const,
  meridianGeographies: (stateFips?: string) => ['meridian', 'geographies', stateFips] as const,
  modelVersions: (params: ModelVersionParams) => ['reference', 'model-versions', params] as const,
  surveyReconciliation: (params: ReconciliationParams) => ['meridian', 'survey-reconciliation', params] as const,
  surveyCountyDistribution: (params: CountyDistributionParams | null) => ['meridian', 'survey-county-distribution', params] as const,
};

export function useDemoManifest(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.demoManifest,
    queryFn: () => apiClient.getRaw<DemoManifest>('/demo/manifest'),
    staleTime: Infinity,
    retry: false,
    enabled: options?.enabled ?? true,
  });
}

export function useDemoBriefing(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.demoBriefing,
    queryFn: () => apiClient.getRaw<DemoBriefing>('/demo/briefing'),
    staleTime: Infinity,
    retry: false,
    enabled: options?.enabled ?? true,
  });
}

export function useSession(options?: Partial<UseQueryOptions<Session>>) {
  return useQuery<Session>({
    queryKey: qk.session,
    queryFn: async () => {
      const res = await apiClient.get<Session>('/auth/session');
      return res.data;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LoginRequest) => apiClient.post<Session>('/auth/login', payload),
    onSuccess: (res) => {
      qc.setQueryData(qk.session, res.data);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post('/auth/logout'),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SignupRequest) => apiClient.post<Session>('/auth/signup', payload),
    onSuccess: (res) => {
      qc.setQueryData(qk.session, res.data);
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => apiClient.post('/auth/forgot-password', { email }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      apiClient.post('/auth/reset-password', input),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => apiClient.post('/auth/verify-email', { token }),
  });
}

export function useAssets(params?: PaginationParams) {
  return useQuery({
    queryKey: qk.assets(params),
    queryFn: async () => {
      const res = await apiClient.get<Asset[]>('/assets', params as Record<string, unknown>);
      return res;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useAsset(id: string) {
  return useQuery({
    queryKey: qk.asset(id),
    queryFn: async () => {
      const res = await apiClient.get<Asset>(`/assets/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAssetRequest) => apiClient.post<Asset>('/assets', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: qk.tenantCapabilities() });
    },
  });
}

export function useUpdateAsset(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAssetRequest) => apiClient.patch<Asset>(`/assets/${id}`, payload),
    onSuccess: (res) => {
      qc.setQueryData(qk.asset(id), res.data);
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/assets/${id}`),
    onSuccess: (_res, id) => {
      qc.removeQueries({ queryKey: qk.asset(id) });
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: qk.tenantCapabilities() });
    },
  });
}

export function useAois(assetId: string) {
  return useQuery({
    queryKey: qk.aois(assetId),
    queryFn: async () => {
      const res = await apiClient.get<Aoi[]>(`/assets/${assetId}/aois`);
      return res.data;
    },
    enabled: !!assetId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAoi(id: string) {
  return useQuery({
    queryKey: qk.aoi(id),
    queryFn: async () => {
      const res = await apiClient.get<Aoi>(`/aois/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateAoi(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAoiRequest) => apiClient.post<Aoi>(`/assets/${assetId}/aois`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.aois(assetId) });
    },
  });
}

export function useObservations(params: ObservationQueryParams) {
  return useQuery({
    queryKey: qk.observations(params),
    queryFn: async () => {
      const res = await apiClient.get<Observation[]>('/observations', params as Record<string, unknown>);
      return res;
    },
    enabled: !!(params.assetId ?? params.aoiId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useObservationsSummary(assetId: string) {
  return useQuery({
    queryKey: qk.observationsSummary(assetId),
    queryFn: async () => {
      const res = await apiClient.get<ObservationSummary>(`/assets/${assetId}/observations/summary`);
      return res.data;
    },
    enabled: !!assetId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAlerts(params?: AlertQueryParams) {
  return useQuery({
    queryKey: qk.alerts(params),
    queryFn: async () => {
      const res = await apiClient.get<Alert[]>('/alerts', params as Record<string, unknown>);
      return res;
    },
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });
}

export function useConditionData(filters: FilterParams, latest = false) {
  return useQuery({
    queryKey: [...qk.meridianCondition(filters), latest],
    queryFn: async () =>
      apiClient.getRaw<ConditionResponse>('/v1/statistics/condition', { ...filters, ...(latest ? { season_year: undefined, week: undefined } : {}) }),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useCropCapabilities(filters?: CropCapabilityParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.cropCapabilities(filters),
    queryFn: async () =>
      apiClient.getRaw<CropCapability[]>('/v1/crop-capabilities', filters as Record<string, unknown>),
    enabled: options?.enabled ?? true,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function usePredictions(filters?: PredictionParams) {
  return useQuery({
    queryKey: qk.predictions(filters),
    queryFn: async () =>
      apiClient.getRaw<PredictionsResponse>('/v1/predictions', filters as Record<string, unknown>),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function usePredictionValidation(filters?: PredictionParams) {
  return useQuery({
    queryKey: qk.predictionValidation(filters),
    queryFn: async () =>
      apiClient.getRaw<PredictionValidationResponse>(
        '/v1/predictions/validation',
        filters as Record<string, unknown>,
      ),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useProgressData(filters: FilterParams) {
  return useQuery({
    queryKey: qk.meridianProgress(filters),
    queryFn: async () =>
      apiClient.getRaw<ProgressResponse>('/v1/statistics/progress', filters as Record<string, unknown>),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useEventsData(filters: EventFilterParams) {
  return useQuery({
    queryKey: qk.meridianEvents(filters),
    queryFn: async () => {
      const res = await apiClient.get<EventsResponse>('/v1/statistics/events', filters as Record<string, unknown>);
      return res.data;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useSoilMoistureData(params: {
  state_fips?: string;
  county_fips?: string;
  date_start: string;
  date_end: string;
}) {
  return useQuery({
    queryKey: qk.meridianSoilMoisture(params),
    queryFn: async () => {
      const res = await apiClient.get<SoilMoistureResponse>('/v1/statistics/soil-moisture', params as Record<string, unknown>);
      return res.data;
    },
    staleTime: 60 * 1000,
    retry: false,
    enabled: !!(params.date_start && params.date_end),
  });
}

export function useCountyGeographies(stateFips?: string) {
  return useQuery({
    queryKey: qk.meridianGeographies(stateFips),
    queryFn: async () =>
      apiClient.getRaw<GeoJsonFeatureCollection<GeoPolygon | GeoMultiPolygon, CountyGeometryProperties>>(
        '/v1/reference/geographies',
        stateFips ? { state_fips: stateFips, level: 'county' } : { level: 'county' },
      ),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

/**
 * Registered model versions (`GET /v1/reference/model-versions`).
 *
 * The registry is tenant-agnostic and changes only on a promotion, so this is
 * cached long. `activeOnly` maps to the handler's `active_only` query field.
 */
export function useModelVersions(params: ModelVersionParams = {}) {
  return useQuery({
    queryKey: qk.modelVersions(params),
    queryFn: async () => {
      const rows: ModelVersion[] = [];
      let cursor: string | undefined;
      do {
        const page = await apiClient.get<ModelVersion[]>('/v1/reference/model-versions', {
          kind: params.kind, active_only: params.activeOnly, cursor, limit: 100,
        });
        rows.push(...page.data);
        cursor = page.meta?.nextCursor ?? undefined;
      } while (cursor);
      return rows;
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function useReconciliation(params: ReconciliationParams) {
  return useQuery({
    queryKey: qk.surveyReconciliation(params),
    queryFn: async (): Promise<ReconciliationResponse> => {
      if (IS_DEMO_MODE) {
        const response = await fetch('/fixtures/reconciliation-state.json');
        if (!response.ok) throw new Error('Missing reconciliation-state fixture');
        return response.json() as Promise<ReconciliationResponse>;
      }
      return apiClient.getRaw<ReconciliationResponse>('/v1/survey/reconciliation', params as unknown as Record<string, unknown>);
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useCountyDistribution(params: CountyDistributionParams | null) {
  return useQuery({
    queryKey: qk.surveyCountyDistribution(params),
    enabled: params !== null,
    queryFn: async (): Promise<CountyDistributionResponse> => {
      if (IS_DEMO_MODE) {
        const response = await fetch('/fixtures/reconciliation-county-detail.json');
        if (!response.ok) throw new Error('Missing reconciliation-county-detail fixture');
        const all = await response.json() as Record<string, CountyDistributionResponse>;
        const key = `${params!.state_fips}-${params!.crop_type}-${params!.observation_year}-${params!.observation_week}`;
        const fixture = all[key];
        if (!fixture) throw new Error(`No county distribution fixture for ${key}`);
        return fixture;
      }
      return apiClient.getRaw<CountyDistributionResponse>('/v1/predictions', {
        geography_level: 'county',
        state_fips: params!.state_fips,
        crop_type: params!.crop_type,
        season_year: params!.observation_year,
        week: params!.observation_week,
      });
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => apiClient.post<Alert>(`/alerts/${alertId}/acknowledge`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useResolveAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => apiClient.post<Alert>(`/alerts/${alertId}/resolve`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useAlertPolicies() {
  return useQuery({
    queryKey: qk.alertPolicies(),
    queryFn: async () => {
      const res = await apiClient.get<AlertPolicy[]>('/alert-policies');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAlertPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAlertPolicyRequest) => apiClient.post<AlertPolicy>('/alert-policies', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.alertPolicies() });
    },
  });
}

export function useReports() {
  return useQuery({
    queryKey: qk.reports(),
    queryFn: async () => {
      const res = await apiClient.get<Report[]>('/reports');
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GenerateReportRequest) => apiClient.post<Report>('/reports', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.reports() });
    },
  });
}

export function useDownloadReport() {
  return useMutation({
    mutationFn: async (reportId: string): Promise<string> => {
      const res = await apiClient.get<{ url: string }>(`/reports/${reportId}/download`);
      return res.data.url;
    },
    onSuccess: (url) => {
      window.open(url, '_blank');
    },
  });
}

export function useTenantSettings() {
  return useQuery({
    queryKey: qk.tenant(),
    queryFn: async () => {
      const res = await apiClient.get<Tenant>('/tenant');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useTenantCapabilities() {
  return useQuery({
    queryKey: qk.tenantCapabilities(),
    queryFn: async () => {
      const res = await apiClient.get<TenantCapabilities>('/tenant/capabilities');
      return res.data;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export type { ApiResponse };
